/**
 * RFP (Request For Proposal) Form Handler
 * Manages the RFP form state and functionality including file uploads,
 * form submission, and form reset capabilities.
 * @returns {Object} Alpine.js component object
 */
export function rfpForm() {
    return {
        // Form submission state
        submitted: false,
        submissionMessage: '',

        // File upload state
        files: [],
        dragging: false,

        // Main form data object
        form: {
            // Company Information
            contactName: '',
            email: '',
            phone: '',

            // Project Details
            location: '',
            buildingType: '',
            otherBuildingType: '',
            products: [],

            // Elevator Specifications
            elevatorQuantity: '',
            elevatorType: '',
            elevatorCapacity: '',
            elevatorSpeed: '',
            elevatorFloors: '',
            elevatorFeatures: '',

            // Escalator Specifications
            escalatorQuantity: '',
            escalatorType: '',
            escalatorWidth: '',
            escalatorAngle: '',
            escalatorRise: '',
            escalatorFeatures: '',

            // Travolator Specifications
            travolatorQuantity: '',
            travolatorType: '',
            travolatorWidth: '',
            travolatorLength: '',
            travolatorSpeed: '',
            travolatorFeatures: '',

            // Additional Requirements
            serviceMaintenance: '',
            accessibility: '',
            energy: '',
            design: '',
            additionalInfo: ''
        },

        /**
         * Validates and processes files for upload
         * @param {Array} files - Array of File objects to process
         * @returns {Array} - Array of valid files
         */
        validateFiles(files) {
            const allowedTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'image/jpeg',
                'image/png',
            ];
            const maxSize = 10 * 1024 * 1024; // 10MB

            return files.filter(file => {
                if (!allowedTypes.includes(file.type)) {
                    alert(`Файл ${file.name} не дозволений. Завантажуйте тільки PDF, DOC, JPG або PNG.`);
                    return false;
                }
                if (file.size > maxSize) {
                    alert(`Файл ${file.name} занадто великий. Максимальний розмір — 10MB.`);
                    return false;
                }
                return true;
            });
        },

        /**
         * Uploads valid files to the server
         * @param {Array} validFiles - Array of validated File objects
         */
        uploadFiles(validFiles) {
            // Create FormData and append valid files
            const formData = new FormData();
            validFiles.forEach(file => {
                formData.append('files[]', file);
            });

            // Upload files
            fetch(window.location.href, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-File-Upload': 'true'
                },
                body: formData
            })
                .then(response => response.json())
                .then(data => {
                    if (data.status === 'success') {
                        this.files = [...this.files, ...validFiles];
                    } else {
                        alert('Error uploading files: ' + data.message);
                    }
                })
                .catch(error => {
                    console.error('Upload error:', error);
                    alert('Error uploading files. Please try again.');
                });

            // Reset the input to allow selecting the same file again
            const fileInput = document.getElementById('file-upload');
            if (fileInput) {
                fileInput.value = '';
            }
        },

        /**
         * Handles file upload from input change event
         * @param {Event} event - File input change event
         */
        handleFileUpload(event) {
            const files = Array.from(event.target.files || []);
            const validFiles = this.validateFiles(files);

            if (validFiles.length > 0) {
                this.uploadFiles(validFiles);
            }
        },

        /**
         * Removes a file from the files array
         * @param {String} fileName - Name of the file to remove
         */
        removeFile(fileName) {
            this.files = this.files.filter(file => file.name !== fileName);
        },

        /**
         * Handles drag and drop file upload
         * @param {Event} event - Drop event
         */
        handleDrop(event) {
            this.dragging = false;
            event.preventDefault();

            const files = Array.from(event.dataTransfer.files || []);
            const validFiles = this.validateFiles(files);

            if (validFiles.length > 0) {
                this.uploadFiles(validFiles);
            }
        },

        /**
         * Prevents default behavior during drag over
         * @param {Event} event - Drag over event
         */
        handleDragOver(event) {
            event.preventDefault();
        },

        /**
         * Sets dragging state to true when files are dragged into the drop zone
         * @param {Event} event - Drag enter event
         */
        handleDragEnter(event) {
            event.preventDefault();
            this.dragging = true;
        },

        /**
         * Sets dragging state to false when leaving the drop zone
         * @param {Event} event - Drag leave event
         */
        handleDragLeave(event) {
            // Only set dragging to false if we're leaving the drop zone
            // and not entering a child element
            if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) {
                this.dragging = false;
            }
        },

        /**
         * Handles form submission
         * Sends form data to server and handles response
         */
        submitForm() {
            // Send form data to server
            fetch(window.location.href, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(this.form)
            })
                .then(response => {
                    // Parse response and handle potential JSON parsing errors
                    return response.text().then(text => {
                        console.log('Raw response:', text);
                        try {
                            return JSON.parse(text);
                        } catch (error) {
                            throw new Error(`Invalid JSON response: ${text}`);
                        }
                    });
                })
                .then(data => {
                    // Update UI on successful submission
                    this.submissionMessage = `Ваші вимоги до проекту були успішно надіслані. <br>
                Наші фахівці вже працюють над їхньою оцінкою, і незабаром ми зв'яжемося з вами.`;
                    this.submitted = true;
                })
                .catch(error => {
                    console.error('Submission error:', error);
                    alert(`An error occurred while submitting the form: ${error.message}`);
                });
        },

        /**
         * Resets the form to its initial state
         * Clears all form fields, files, and submission status
         */
        resetForm() {
            // Reset submission state
            this.submitted = false;
            this.submissionMessage = '';
            this.files = [];
            this.dragging = false;

            // Reset form data to initial empty state
            this.form = Object.keys(this.form).reduce((acc, key) => {
                acc[key] = Array.isArray(this.form[key]) ? [] : '';
                return acc;
            }, {});

            // Clear file input
            const fileInput = document.getElementById('file-upload');
            if (fileInput) {
                fileInput.value = '';
            }
        }
    }
}
